import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';
import Sidebar from './Sidebar';
import DashboardHome from './DashboardHome';
import DataTables from './DataTables';
import TableView from './TableView';
import QueryView from './QueryView';
import StationInfo from './StationInfo';
import Location from './Location';
import Wayleave from './Wayleave';
import Reports from './Reports';
import Settings from './Settings';
import HelpFeedback from './HelpFeedback';
import NotificationBell from './NotificationBell';
import DataAssistant from './DataAssistant';
import { SwitchUserMenu, ImpersonationBanner } from './ImpersonationControls';

// Ordered screen → path map, used to pick a user's default landing page.
const SCREEN_PATHS = [
  ['dashboard', '/'],
  ['station-info', '/station-info'],
  ['location', '/location'],
  ['wayleave', '/wayleave'],
  ['reports', '/reports'],
  ['data', '/data'],
];

function getDefaultPath(screens) {
  const match = SCREEN_PATHS.find(([s]) => screens.includes(s));
  return match ? match[1] : '/help';
}

// Protected route component — redirects to the user's first accessible screen.
function ProtectedRoute({ children, screen, screens, fallback }) {
  if (!screens.includes(screen)) {
    return <Navigate to={fallback} replace />;
  }
  return children;
}

function Dashboard({ onLogout, userInfo, onUserChange }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentTableLabel, setCurrentTableLabel] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [currentTableName, setCurrentTableName] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const userRole = userInfo?.role || 'client';
  const screens = userInfo?.screens || [];
  const defaultPath = getDefaultPath(screens);
  const impersonating = !!userInfo?.impersonating;
  // A real System Administrator (i.e. not one currently being impersonated).
  const canSwitchUser = userRole === 'sysadmin' && !impersonating;

  // Start impersonating: swap identity server-side, refresh auth, land on home.
  const handleImpersonate = async (userId) => {
    await authAPI.impersonate(userId);
    if (onUserChange) await onUserChange();
    navigate('/');
  };

  // Return to the real System Administrator account.
  const handleStopImpersonate = async () => {
    await authAPI.stopImpersonate();
    if (onUserChange) await onUserChange();
    navigate('/');
  };

  useEffect(() => {
    // Extract table name from URL path like /data/table/TEST_FDS
    const match = location.pathname.match(/\/data\/table\/(.+)/);
    if (match) {
      const tableName = match[1];
      setCurrentTableName(tableName);

      // Load custom label from localStorage
      const saved = localStorage.getItem('tableLabels');
      if (saved) {
        try {
          const labels = JSON.parse(saved);
          setCurrentTableLabel(labels[tableName] || '');
        } catch (e) {
          console.error('Failed to load table labels:', e);
        }
      }
    } else {
      setCurrentTableName('');
      setCurrentTableLabel('');
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        isCollapsed={sidebarCollapsed}
        toggleCollapsed={() => setSidebarCollapsed(!sidebarCollapsed)}
        onLogout={onLogout}
        userRole={userRole}
        screens={screens}
      />

      {/* Main Content Area */}
      <div className={`flex-1 min-w-0 flex flex-col min-h-screen transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-14' : 'lg:ml-56'}`}>
        {/* Impersonation banner (shown only while impersonating) */}
        {impersonating && (
          <ImpersonationBanner
            targetUsername={userInfo?.username}
            targetRole={userRole}
            impersonatorUsername={userInfo?.impersonator?.username}
            onStop={handleStopImpersonate}
          />
        )}

        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                {/* Mobile menu button */}
                <button
                  onClick={toggleSidebar}
                  className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 mr-4"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div className="flex items-center gap-2">
                  <h1 className="text-sm font-semibold text-gray-900">Traffic Counting Station (TCS) Information & Analysis</h1>
                  {currentTableLabel && (
                    <>
                      <span className="text-gray-400">/</span>
                      <span className="text-xs font-medium text-primary-600">
                        {currentTableLabel}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canSwitchUser && (
                  <SwitchUserMenu currentUserId={userInfo?.userId} onImpersonate={handleImpersonate} />
                )}
                <NotificationBell />
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 px-4 py-8 sm:px-6 lg:px-8 overflow-x-hidden">
          <Routes>
            <Route
              path="/"
              element={
                screens.includes('dashboard')
                  ? <DashboardHome />
                  : <Navigate to={defaultPath} replace />
              }
            />
            <Route
              path="/station-info"
              element={
                <ProtectedRoute screen="station-info" screens={screens} fallback={defaultPath}>
                  <StationInfo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute screen="reports" screens={screens} fallback={defaultPath}>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data"
              element={
                <ProtectedRoute screen="data" screens={screens} fallback={defaultPath}>
                  <DataTables />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data/table/:tableName"
              element={
                <ProtectedRoute screen="data" screens={screens} fallback={defaultPath}>
                  <TableView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data/query/:queryId"
              element={
                <ProtectedRoute screen="data" screens={screens} fallback={defaultPath}>
                  <QueryView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/location"
              element={
                <ProtectedRoute screen="location" screens={screens} fallback={defaultPath}>
                  <Location userInfo={userInfo} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wayleave"
              element={
                <ProtectedRoute screen="wayleave" screens={screens} fallback={defaultPath}>
                  <Wayleave userInfo={userInfo} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute screen="settings" screens={screens} fallback={defaultPath}>
                  <Settings userRole={userRole} />
                </ProtectedRoute>
              }
            />
            <Route path="/help" element={<HelpFeedback userInfo={userInfo} />} />
          </Routes>
        </main>
      </div>

      {/* Floating AI Data Assistant (available on every page) */}
      <DataAssistant />
    </div>
  );
}

export default Dashboard;
